import { motion } from "framer-motion";
import { ArrowLeft, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import CTAButton from "./CTAButton";
import Navbar from "./Navbar";
import Footer from "./Footer";

interface Subcategory {
  image: string;
  title: string;
  description: string;
  slug: string;
}

interface CategoryPageLayoutProps {
  heading: string;
  subtitle: string;
  basePath: string;
  subcategories: Subcategory[];
}

const CategoryPageLayout = ({ heading, subtitle, basePath, subcategories }: CategoryPageLayoutProps) => {
  return (
    <div className="overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="section-light pt-32 pb-16 md:pt-40 md:pb-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/#services"
              className="inline-flex items-center gap-2 text-section-light-foreground/50 hover:text-section-light-foreground text-sm font-body tracking-wider uppercase transition-colors duration-300 mb-10"
            >
              <ArrowLeft size={16} />
              Tutti i servizi
            </Link>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-section-light-foreground uppercase leading-[1.05] max-w-4xl"
          >
            {heading}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-6 text-section-light-foreground/50 font-body text-base md:text-lg max-w-xl"
          >
            {subtitle}
          </motion.p>
        </div>
      </section>

      {/* Subcategories Grid */}
      <section className="section-light pb-24 md:pb-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {subcategories.map((sub, i) => (
              <motion.div
                key={sub.slug}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15 + i * 0.1 }}
                className="group"
              >
                <Link to={`${basePath}/${sub.slug}`}>
                  <div className="aspect-square overflow-hidden rounded-sm relative cursor-pointer">
                    <img
                      src={sub.image}
                      alt={sub.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center gap-2">
                      <Eye size={28} className="text-foreground" />
                      <span className="text-foreground text-xs tracking-widest uppercase font-body">Scopri di più</span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <h3 className="font-heading text-base font-semibold text-section-light-foreground uppercase tracking-wide">
                      {sub.title}
                    </h3>
                    <p className="mt-1 text-section-light-foreground/40 font-body text-sm">
                      {sub.description}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-light pb-24 md:pb-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-heading text-2xl md:text-4xl font-bold text-section-light-foreground uppercase"
          >
            Hai un progetto in mente?
          </motion.h2>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8"
          >
            <CTAButton className="cta-button-light hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-300" />
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CategoryPageLayout;
